import React, { useMemo } from "react";

const INTERNAL_TERM_LABELS: Array<[RegExp, string]> = [
  [/\brejected_domain_reason\b/g, "除外理由"],
  [/\bdisallowed_company_url_domain\b/g, "公式サイト候補ではないドメイン"],
  [/\bweak_official_evidence\b/g, "公式サイトと判断する根拠が弱い"],
  [/\bcomparison_or_intro_domain\b/g, "比較・紹介サイト"],
  [/\bexcluded_same_name_candidates\b/g, "除外した同名候補"],
  [/\brejected same-name candidates\b/gi, "除外した同名候補"],
  [/\bexcluded same-name candidates\b/gi, "除外した同名候補"],
];

export function normalizeCompanyResearchText(text: string) {
  return INTERNAL_TERM_LABELS.reduce((current, [pattern, label]) => current.replace(pattern, label), text);
}

function trimUrl(rawUrl: string) {
  const match = rawUrl.match(/^(.*?)([)、。.,）」』】\]]*)$/);
  return {
    url: match?.[1] || rawUrl,
    trailing: match?.[2] || "",
  };
}

function pushPlainTextWithLinks(parts: React.ReactNode[], text: string, keyPrefix: string) {
  const urlPattern = /https?:\/\/[^\s<]+/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = urlPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const { url, trailing } = trimUrl(match[0]);
    parts.push(
      <a
        key={`${keyPrefix}-url-${match.index}-${url}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="break-words font-bold text-[#A67C52] underline underline-offset-2 [overflow-wrap:anywhere]"
      >
        {url}
      </a>
    );
    if (trailing) {
      parts.push(trailing);
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
}

function splitMarkdownInline(text: string) {
  const parts: React.ReactNode[] = [];
  const linkPattern = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = linkPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      pushPlainTextWithLinks(parts, text.slice(lastIndex, match.index), `plain-${match.index}`);
    }
    parts.push(
      <a
        key={`${match.index}-${match[2]}`}
        href={match[2]}
        target="_blank"
        rel="noopener noreferrer"
        className="break-words font-bold text-[#A67C52] underline underline-offset-2 [overflow-wrap:anywhere]"
      >
        {match[1]}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    pushPlainTextWithLinks(parts, text.slice(lastIndex), `plain-tail-${lastIndex}`);
  }

  return parts.length > 0 ? parts : text;
}

function getHypothesisBody(text: string) {
  const match = text.match(/^仮説[:：]\s*(.+)$/);
  return match?.[1];
}

function HypothesisCallout({ text }: { text: string }) {
  return (
    <span className="my-1 flex items-start gap-2 rounded-xl border border-[#F0E0D2] bg-[#FFF9F2] px-3 py-2 text-sm leading-6 text-[#5D4D46]">
      <span className="mt-0.5 shrink-0 rounded-full bg-white px-2 py-0.5 text-[10px] font-black text-[#A46D4C]">
        仮説
      </span>
      <span className="min-w-0 break-words [overflow-wrap:anywhere]">{splitMarkdownInline(text)}</span>
    </span>
  );
}

export function CompanyResearchMarkdown({ text }: { text: string }) {
  const normalizedText = useMemo(() => normalizeCompanyResearchText(text), [text]);
  const lines = useMemo(() => normalizedText.split(/\r?\n/), [normalizedText]);
  const elements: React.ReactNode[] = [];
  let listItems: string[] = [];

  const flushList = () => {
    if (listItems.length === 0) return;
    const current = listItems;
    listItems = [];
    elements.push(
      <ul
        key={`list-${elements.length}`}
        className="my-3 list-disc space-y-2 break-words pl-5 text-sm leading-7 text-[#5D4D46] [overflow-wrap:anywhere]"
      >
        {current.map((item, index) => (
          <li key={`${item}-${index}`} className="min-w-0">
            {getHypothesisBody(item) ? (
              <HypothesisCallout text={getHypothesisBody(item) || item} />
            ) : (
              splitMarkdownInline(item)
            )}
          </li>
        ))}
      </ul>
    );
  };

  lines.forEach((rawLine, index) => {
    const line = rawLine.trim();
    if (!line) {
      flushList();
      return;
    }

    if (line.startsWith("## ")) {
      flushList();
      elements.push(
        <h2
          key={`h2-${index}`}
          className="mt-6 break-words text-lg font-black text-[#5D4D46] first:mt-0 [overflow-wrap:anywhere]"
        >
          {splitMarkdownInline(line.slice(3))}
        </h2>
      );
      return;
    }

    if (line.startsWith("# ")) {
      flushList();
      elements.push(
        <h1
          key={`h1-${index}`}
          className="mt-6 break-words text-xl font-black text-[#5D4D46] first:mt-0 [overflow-wrap:anywhere]"
        >
          {splitMarkdownInline(line.slice(2))}
        </h1>
      );
      return;
    }

    if (line.startsWith("- ")) {
      listItems.push(line.slice(2));
      return;
    }

    const hypothesisBody = getHypothesisBody(line);
    if (hypothesisBody) {
      flushList();
      elements.push(<HypothesisCallout key={`hypothesis-${index}`} text={hypothesisBody} />);
      return;
    }

    flushList();
    elements.push(
      <p
        key={`p-${index}`}
        className="my-3 break-words text-sm leading-7 text-[#5D4D46] [overflow-wrap:anywhere]"
      >
        {splitMarkdownInline(line)}
      </p>
    );
  });

  flushList();
  return <div>{elements}</div>;
}
